import axios, { Canceler, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios'
import aqe, { SessionState, createSessionErrorInterceptor } from '@pushly/aqe'
import { Container } from 'typescript-ioc/es5'
import { AuthService } from '@halo/services/auth.service'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { shouldUseLegacyAuthHeader } from '@halo/_utils/session-helpers'
import { PushlyCookie } from '@halo/enums/pushly-cookie.enum'
import { initUserActivityTracker } from '@pushly/aqe/lib/utils/session/user-activity-tracker'
import { initCrossTabSessionSync } from '@pushly/aqe/lib/utils/session/session-state-sync'
import { handleSessionResponse, applyUserActivityHeader } from '@pushly/aqe/lib/utils/session/session-interceptor'
import { installSessionDebug } from '@pushly/aqe/lib/utils/session/session-debug'

initUserActivityTracker()

const sessionState = Container.get(SessionState)

// Cross-tab session sync: when another tab updates session state, apply it locally
initCrossTabSessionSync({
    getState: () => Container.get(SessionState),
})

export function isLocalEnv() {
    return process.env.LOCAL_ENV?.toString() === 'true'
}

function haloRequestInterceptor(request: any) {
    if (request.external === true) {
        return request
    }

    try {
        if (sessionState.sessionExpired) {
            return Promise.reject(new axios.Cancel('Session expired — request blocked'))
        }
    } catch {
        // Container not ready yet — allow the request
    }

    request.withCredentials = true
    request.headers['X-Application-Source'] = 'halo'
    request.headers['X-Application-Version'] = 'halo'

    if (shouldUseLegacyAuthHeader()) {
        request.headers.Authorization = `Bearer ${window.localStorage.getItem(PushlyCookie.PLATFORM_COOKIE)}`
    }

    applyUserActivityHeader(request.headers, request, aqe.defaults.publicApiDomain, request.url)

    return request
}

const haloReponseErrorInterceptor = createSessionErrorInterceptor(sessionState, {
    notify429: (msg) => {
        const notifDescription = `Pushly was unable to complete your request. ${msg.replace(/\.$/, '')}.`

        simpleNotification('error', notifDescription, 30, 'rate-limit-exception')
    },
})

function haloResponseInterceptor(response: AxiosResponse) {
    try {
        handleSessionResponse(response, sessionState)
    } catch (error) {
        console.warn('Error updating session state from headers:', error)
    }

    return response
}

export const setupAxios = () => {
    axios.interceptors.request.use(haloRequestInterceptor)
    aqe.useAxiosInterceptor('request', haloRequestInterceptor)

    // parse session headers on success, implement global error handlers
    axios.interceptors.response.use(haloResponseInterceptor, haloReponseErrorInterceptor)
    aqe.useAxiosInterceptor('response', haloResponseInterceptor as any, haloReponseErrorInterceptor)

    // Expose session debug helpers on window.__sessionDebug in all environments
    // (incl. deployed dev/beta, which build with NODE_ENV=production). The
    // helper only affects the current tab's client-side session UX, so it's safe
    // outside local dev.
    try {
        installSessionDebug(sessionState, { enabled: true })
    } catch {}
}

export const axiosCancellationRequests: { [index: string]: Canceler } = {}
export const isAxiosCancellation = (error: any) => axios.isCancel(error)
export const isAxiosResponseError = (error: Error): error is AxiosError => !!(error as AxiosError).response

export type AxiosMethod = 'get' | 'put' | 'post' | 'patch' | 'delete'
interface IAxiosRequest {
    url: string
    data?: any
    config?: AxiosRequestConfig & { cancelToken?: any }
}

export async function axiosFetch(
    method: AxiosMethod,
    pack: IAxiosRequest,
    cancellationKey?: string,
): Promise<AxiosResponse<any>> {
    pack.config = pack.config || {}

    const args: any = [pack.url]
    if (pack.data) args.push(pack.data)

    if (cancellationKey) pack.config.cancelToken = await buildCancellationRequest(cancellationKey)
    args.push(pack.config)

    let fetcher = axios.get
    switch (method.toLowerCase()) {
        case 'post':
            fetcher = axios.post
            break
        case 'patch':
            fetcher = axios.patch
            break
        case 'delete':
            fetcher = axios.delete
            break
    }

    // @ts-ignore
    const response = await fetcher(...args)

    if (cancellationKey && axiosCancellationRequests[cancellationKey]) delete axiosCancellationRequests[cancellationKey]

    return response
}

const buildCancellationRequest = async (cancellationKey: string): Promise<any> => {
    const handler = axiosCancellationRequests[cancellationKey]

    if (handler) {
        await handler()
        delete axiosCancellationRequests[cancellationKey]
    }

    return new axios.CancelToken((cb) => {
        axiosCancellationRequests[cancellationKey] = cb
    })
}
